import { SortQualifier } from '@core/filters/path-filter/dsl/search-bar';
import { KPI_FIELD_PREFIX } from '@core/filters/path-filter/kpi-names';
import {
  FieldType,
  FilterVocabulary,
  SortableField
} from '@core/filters/path-filter/vocabulary/vocabulary';
import { fieldPath } from '@core/filters/path-filter/wire';
import { OrderBy } from '@core/models/entity/entity-selector-request.model';

export type SortTarget = Omit<SortQualifier, 'order'>;

export interface SortOption {
  key: string;
  label: string;
  target: SortTarget;
  /** The owner the target belongs to: the entity's name, or the association's label. */
  group: string;
  kpi: boolean;
  /** Words the direction ("A to Z", "Longest first"); null when the vocabulary does not type it. */
  type: FieldType | null;
}

/**
 * A leaf behind a many edge is offered once per aggregate: the data layer refuses a sort that does
 * not say which related row decides the order.
 */
export function sortOptions(
  vocabulary: FilterVocabulary,
  labelOf: (sortable: SortableField) => string,
  aggregateLabel: (aggregate: 'min' | 'max') => string,
  groupOf: (edge: string | null) => string = (edge) => edge ?? vocabulary.entityType
): SortOption[] {
  return vocabulary.sortable().flatMap((sortable): SortOption[] => {
    const label = labelOf(sortable);
    const association = sortable.association;
    const edge = association?.edge ?? null;
    const shared = {
      group: groupOf(edge),
      kpi: sortable.field.startsWith(KPI_FIELD_PREFIX),
      type: vocabulary.field(edge, fieldPath(sortable.field))?.type ?? null
    };
    if (!association) {
      return [{ key: sortable.field, label, target: { field: sortable.field }, ...shared }];
    }
    if (association.cardinality === 'one') {
      return [
        {
          key: `${association.edge}.${sortable.field}`,
          label,
          target: { field: sortable.field, association: { edge: association.edge } },
          ...shared
        }
      ];
    }
    return (['min', 'max'] as const).map((aggregate) => ({
      key: `${association.edge}.${sortable.field}-${aggregate}`,
      label: `${label} (${aggregateLabel(aggregate)})`,
      target: { field: sortable.field, association: { edge: association.edge, aggregate } },
      ...shared
    }));
  });
}

/**
 * The grid's column sorts the vocabulary does not offer, all owned by the entity. KPI sorts, keyed
 * ones included, are only known this way: the vocabulary does not mark them sortable.
 */
export function columnSortOptions(
  columns: { dbColName: string; label: string; type: FieldType | null }[],
  known: SortOption[],
  group: string
): SortOption[] {
  const offered = new Set<string>();
  return columns.flatMap(({ dbColName, label, type }): SortOption[] => {
    const target = { field: dbColName };
    if (
      offered.has(dbColName) ||
      known.some((option) => sameSortTarget(option.target, target))
    )
      return [];
    offered.add(dbColName);
    return [
      {
        key: dbColName,
        label,
        target,
        group,
        kpi: dbColName.startsWith(KPI_FIELD_PREFIX),
        type
      }
    ];
  });
}

export function sameSortTarget(a: SortTarget, b: SortTarget): boolean {
  return (
    a.field === b.field &&
    a.association?.edge === b.association?.edge &&
    a.association?.aggregate === b.association?.aggregate
  );
}

export function toOrderBy(qualifier: SortQualifier): OrderBy {
  return {
    field: qualifier.field,
    order: qualifier.order,
    ...(qualifier.association ? { association: qualifier.association } : {})
  };
}

/** Sorts picked in the Path bar come first; column headers still refine after them. */
export function pathOrderBy(
  sort: SortQualifier[],
  columns: OrderBy[] | undefined
): OrderBy[] | undefined {
  const merged = [...sort.map(toOrderBy), ...(columns ?? [])];
  return merged.length > 0 ? merged : undefined;
}

export function toSortQualifier(orderBy: OrderBy): SortQualifier {
  return {
    field: orderBy.field,
    order: orderBy.order,
    ...(orderBy.association ? { association: orderBy.association } : {})
  };
}
